/**
 * MainSettingsPage manages the settings view including polar file selection,
 * calculated wind options, and race vessel configuration.
 * 
 * Responsibilities:
 * - Managing settings UI display and updates
 * - Handling polar file selection via Google Drive picker
 * - Managing calculated wind options (enabled, useSOG, useLeeway, leewayK)
 * - Managing race vessel list (names, colors, handicaps)
 * - Managing handicap columns
 * - Validating leeway K values (0-20 range)
 * - Persisting settings to Google Drive
 * - Enabling/disabling dependent checkboxes based on calculated wind toggle
 */

import { BOAT_COLORS_ARR } from '../domain/Race.js';
import { RaceVessel } from '../domain/Settings.js';

export class MainSettingsPage {
  constructor(dataStore, driveHelpers, commonModule, settings) {
    this.dataStore = dataStore;
    this.drive = driveHelpers;
    this.common = commonModule;
    this.settings = settings;
    this.saveInProgress = false;
    this.pendingSave = false;
    
    this.currentPolarFileNameElement = null;
    this.useCalculatedWindCheckbox = null;
    this.useSOGCheckbox = null;
    this.useLeewayCheckbox = null;
    this.leewayKInput = null;
    this.choosePolarButton = null;
    this.clearPolarButton = null;
    this.settingsButton = null;
    this.raceVesselsTableContainer = null;
    this.vesselNameInput = null;
  }

  init() {
    this.currentPolarFileNameElement = document.getElementById('currentPolarFileName');
    this.useCalculatedWindCheckbox = document.getElementById('useCalculatedWindCheckbox');
    this.useSOGCheckbox = document.getElementById('useSOGCheckbox');
    this.useLeewayCheckbox = document.getElementById('useLeewayCheckbox');
    this.leewayKInput = document.getElementById('leewayKInput');
    this.choosePolarButton = document.getElementById('choosePolarButton');
    this.clearPolarButton = document.getElementById('clearPolarButton');
    this.settingsButton = document.getElementById('playerModeButtonSettings');
    this.raceVesselsTableContainer = document.getElementById('raceVesselsTableContainer');
    this.vesselNameInput = document.getElementById('vesselNameInput');

    this.choosePolarButton.addEventListener('click', () => this.choosePolarFile());
    this.clearPolarButton.addEventListener('click', () => this.clearPolarFile());
    this.useCalculatedWindCheckbox.addEventListener('change', (e) => this.updateCalculatedWindEnabled(e.target.checked));
    this.useSOGCheckbox.addEventListener('change', (e) => this.updateCalculatedWindOption('useSOG', e.target.checked));
    this.useLeewayCheckbox.addEventListener('change', (e) => this.updateCalculatedWindOption('useLeeway', e.target.checked));
    this.leewayKInput.addEventListener('change', (e) => this.validateAndUpdateLeewayK(e.target.value));
    this.vesselNameInput.addEventListener('change', (e) => this.updateVesselName(e.target.value));
  }

  updateDisplay() {
    if (!this.settings) return;

    const fileName = this.settings.getDefaultPolarFile();
    this.currentPolarFileNameElement.textContent = fileName || 'None selected';
    
    const calcWind = this.settings.getCalculatedWind();
    const enabled = calcWind.enabled || false;
    this.useCalculatedWindCheckbox.checked = enabled;
    this.useSOGCheckbox.checked = calcWind.useSOG || false;
    this.useSOGCheckbox.disabled = !enabled;
    this.useLeewayCheckbox.checked = calcWind.useLeeway !== false;
    this.useLeewayCheckbox.disabled = !enabled;
    this.leewayKInput.value = calcWind.leewayK || 10;

    const defaultVessel = this.settings.getDefaultVessel();
    this.vesselNameInput.value = defaultVessel.name || '';

    this.updateRaceVesselsDisplay();
  }

  async choosePolarFile() {
    const folder = await this.drive.ensurePolarFolderAsync();
    await this.drive.drivePickAndDownloadFileAsync(folder, async (response, doc) => {
      if (response.ok && doc) {
        try {
          this.common.showLoadingSpinner();
          await this.dataStore.saveDefaultPolarAsync(doc.name);
          await this.dataStore.ensurePolarLoadedAsync();
          this.updateDisplay();
        } catch (error) {
          console.error('Error choosing polar:', error);
          alert(`Error choosing polar: ${error.message}`);
        } finally {
          this.common.hideLoadingSpinner();
        }
      }
    });
  }

  async clearPolarFile() {
    this.settings.clearDefaultPolarFile();
    await this.saveSettings();
  }

  async updateCalculatedWindEnabled(enabled) {
    this.settings.getCalculatedWind().enabled = enabled;
    await this.saveSettings();
  }

  async updateCalculatedWindOption(option, value) {
    this.settings.getCalculatedWind()[option] = value;
    await this.saveSettings();
  }

  async validateAndUpdateLeewayK(value) {
    const numValue = parseFloat(value);
    if (numValue >= 0 && numValue <= 20) {
      this.settings.getCalculatedWind().leewayK = numValue;
      await this.saveSettings();
    } else {
      alert('Leeway K must be between 0 and 20');
      this.leewayKInput.value = this.settings.getCalculatedWind().leewayK || 10;
    }
  }

  async updateVesselName(newName) {
    if (newName && newName.trim()) {
      const trimmedName = newName.trim();
      this.settings.getDefaultVessel().name = trimmedName;
      
      const defaultRaceVessel = this.settings.getDefaultRaceVessel();
      if (defaultRaceVessel) {
        defaultRaceVessel.data.shipName = trimmedName;
        this.updateRaceVesselsDisplay();
      }
      
      await this.saveSettings();
    }
  }

  async saveSettings() { // has race conditions, should be good enough
    if (this.saveInProgress) {
      this.pendingSave = true;
      return;
    }
    
    try {
      this.updateDisplay();
      this.saveInProgress = true;
      this.common.saveButtonSpinnerOn(this.settingsButton);
      await this.dataStore.saveSettingsAsync();
      
      if (this.pendingSave) {
        this.pendingSave = false;
        this.saveInProgress = false;
        await this.saveSettings();
      }
    } catch (error) {
      console.error('Error saving setting:', error);
      alert(`Error saving setting: ${error.message}`);
    } finally {
      this.saveInProgress = false;
      this.common.saveButtonSpinnerOff(this.settingsButton);
    }
  }

  async addHandicapColumn() {
    const name = prompt('Enter handicap column name (e.g., PHRF, IRC):');
    if (name && name.trim()) {
      this.settings.addHandicapName(name.trim());
      await this.saveSettings();
    }
  }

  async updateHandicapName(idx, newName) {
    if (newName && newName.trim()) {
      this.settings.getHandicapNames()[idx] = newName.trim();
      await this.saveSettings();
    } else {
      // invalid value reset to original name
      this.updateDisplay(); 
    }
  }

  async deleteHandicapColumn(idx) {
    if (confirm('Delete this handicap column? All handicap values for this column will be removed.')) {
      this.settings.delHandicapName(idx);
      await this.saveSettings();
    }
  }

  updateRaceVesselsDisplay() {
    if (!this.settings) return;

    const raceVessels = this.settings.getRaceVessels();
    const handicapNames = this.settings.getHandicapNames();
    const availableVessels = this.getAvailableVessels();

    const table = document.createElement('table');
    table.className = 'settings-page-vessel-table';

    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    
    ['Ship Name', 'MMSI', 'Color'].forEach(header => {
      const th = document.createElement('th');
      th.textContent = header;
      th.className = 'settings-page-vessel-th';
      headerRow.appendChild(th);
    });
    
    handicapNames.forEach((name, idx) => {
      if (idx === 0) return;
      const th = document.createElement('th');
      th.className = 'settings-page-vessel-th settings-page-handicap-header';
      
      const nameSpan = document.createElement('span');
      nameSpan.textContent = name;
      nameSpan.contentEditable = true;
      nameSpan.className = 'settings-page-handicap-header-name';
      nameSpan.addEventListener('blur', (e) => this.updateHandicapName(idx, e.target.textContent));
      
      const deleteBtn = document.createElement('button');
      deleteBtn.textContent = '×';
      deleteBtn.className = 'settings-page-handicap-header-delete';
      deleteBtn.title = 'Delete handicap column';
      deleteBtn.addEventListener('click', () => this.deleteHandicapColumn(idx));
      
      th.appendChild(nameSpan);
      th.appendChild(deleteBtn);
      headerRow.appendChild(th);
    });
    
    const addHandicapTh = document.createElement('th');
    addHandicapTh.className = 'settings-page-vessel-th';
    const addHandicapBtn = document.createElement('button');
    addHandicapBtn.textContent = '+';
    addHandicapBtn.className = 'settings-page-handicap-add';
    addHandicapBtn.title = 'Add handicap column';
    addHandicapBtn.addEventListener('click', () => this.addHandicapColumn());
    addHandicapTh.appendChild(addHandicapBtn);
    headerRow.appendChild(addHandicapTh);
    
    const actionsTh = document.createElement('th');
    actionsTh.className = 'settings-page-vessel-th';
    headerRow.appendChild(actionsTh);
    
    thead.appendChild(headerRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');

    raceVessels.forEach((vessel, vesselIdx) => {
      const row = this.createVesselRow(vessel, vesselIdx, handicapNames, false);
      tbody.appendChild(row);
    });

    if (availableVessels.length > 0) {
      const separatorRow = document.createElement('tr');
      const separatorCell = document.createElement('td');
      separatorCell.colSpan = 3 + (handicapNames.length - 1) + 2;
      separatorCell.className = 'settings-page-separator';
      separatorCell.textContent = 'Available Vessels (from current race)';
      separatorRow.appendChild(separatorCell);
      tbody.appendChild(separatorRow);

      availableVessels.forEach(vessel => {
        const row = this.createVesselRow(vessel, null, handicapNames, true);
        tbody.appendChild(row);
      });
    }

    table.appendChild(tbody);
    this.raceVesselsTableContainer.innerHTML = '';
    this.raceVesselsTableContainer.appendChild(table);
  }

  createVesselRow(vessel, vesselIdx, handicapNames, isAvailable) {
    const row = document.createElement('tr');
    row.className = isAvailable ? 'settings-page-vessel-row-available' : 'settings-page-vessel-row';

    const nameCell = document.createElement('td');
    nameCell.className = 'settings-page-vessel-td';
    if (isAvailable) {
      nameCell.textContent = vessel.getName();
    } else if (vessel.getVesselIdx() !== null) {
      nameCell.textContent = vessel.getName();
    } else {
      const nameInput = document.createElement('input');
      nameInput.type = 'text';
      nameInput.value = vessel.getName();
      nameInput.className = 'settings-page-vessel-input';
      nameInput.addEventListener('change', (e) => this.updateRaceVesselName(vesselIdx, e.target.value));
      nameCell.appendChild(nameInput);
    }
    row.appendChild(nameCell);

    const mmsiCell = document.createElement('td');
    mmsiCell.className = 'settings-page-vessel-td';
    mmsiCell.textContent = isAvailable ? (vessel.getMMSI() || '') : (vessel.getMMSI() || '');
    row.appendChild(mmsiCell);

    const colorCell = document.createElement('td');
    colorCell.className = 'settings-page-vessel-td';
    if (isAvailable) {
      const colorBox = document.createElement('div');
      colorBox.className = 'settings-page-color-box';
      colorBox.style.background = vessel.getColor();
      colorCell.appendChild(colorBox);
    } else {
      const colorSelect = document.createElement('select');
      colorSelect.className = 'settings-page-color-select';
      colorSelect.style.background = vessel.getColor();
      BOAT_COLORS_ARR.forEach(color => {
        const option = document.createElement('option');
        option.value = color;
        option.textContent = '';
        option.style.background = color;
        if (color === vessel.getColor()) {
          option.selected = true;
        }
        colorSelect.appendChild(option);
      });
      colorSelect.addEventListener('change', (e) => {
        e.target.style.background = e.target.value;
        this.updateVesselColor(vesselIdx, e.target.value);
      });
      colorCell.appendChild(colorSelect);
    }
    row.appendChild(colorCell);

    handicapNames.forEach((name, hIdx) => {
      if (hIdx === 0) return;
      const handicapCell = document.createElement('td');
      handicapCell.className = 'settings-page-vessel-td';
      if (isAvailable) {
        handicapCell.textContent = '--';
      } else {
        const handicapInput = document.createElement('input');
        handicapInput.type = 'text';
        const handicapStr = vessel.getHandicapStr(hIdx);
        handicapInput.value = handicapStr !== null ? handicapStr : '';
        handicapInput.placeholder = '--';
        handicapInput.className = 'settings-page-handicap-input';
        handicapInput.addEventListener('change', (e) => this.updateVesselHandicap(vesselIdx, hIdx, e.target.value, handicapInput));
        handicapCell.appendChild(handicapInput);
      }
      row.appendChild(handicapCell);
    });

    const actionsCell = document.createElement('td');
    actionsCell.className = 'settings-page-vessel-td';
    if (isAvailable) {
      const addBtn = document.createElement('button');
      addBtn.textContent = 'Add';
      addBtn.className = 'settings-button';
      addBtn.addEventListener('click', () => this.addAvailableVessel(vessel));
      actionsCell.appendChild(addBtn);
    } else if (vessel.getVesselIdx() === null) {
      const deleteBtn = document.createElement('button');
      deleteBtn.textContent = '×';
      deleteBtn.className = 'settings-page-vessel-delete';
      deleteBtn.title = 'Delete vessel';
      deleteBtn.addEventListener('click', () => this.deleteRaceVessel(vesselIdx));
      actionsCell.appendChild(deleteBtn);
    }
    row.appendChild(actionsCell);

    return row;
  }

  getAvailableVessels() {
    const race = this.dataStore.getRace();
    if (!race) return [];

    if (!this.settings) return [];

    const registeredIds = new Set(
      this.settings.getRaceVessels().map(v => v.getVesselId())
    );

    const available = [];
    const tracks = race.sail().getTracks();
    
    tracks.forEach((track, idx) => {
      const vessel = new RaceVessel({
        shipName: track.getShipName(),
        mmsi: track.isTypeVessel() ? null : track.getMMSI(),
        vesselIdx: track.isTypeVessel() ? idx : null,
        color: track.getColor()
      });
      
      if (!registeredIds.has(vessel.getVesselId())) {
        available.push(vessel);
      }
    });

    return available;
  }

  async addAvailableVessel(vessel) {
    this.settings.addRaceVessel(vessel.data);
    // this.updateRaceVesselsDisplay();
    await this.saveSettings();
  }

  async updateRaceVesselName(vesselIdx, newName) {
    this.settings.getRaceVessels()[vesselIdx].data.shipName = newName;
    await this.saveSettings();
  }

  async updateVesselColor(vesselIdx, newColor) {
    this.settings.getRaceVessels()[vesselIdx].data.color = newColor;
    await this.saveSettings();
  }

  async updateVesselHandicap(vesselIdx, handicapIdx, value, inputElement) {
    const vessel = this.settings.getRaceVessels()[vesselIdx];
    const parsedValue = value.trim() === '' ? null : value.trim();
    vessel.setHandicap(handicapIdx, parsedValue);
    
    const hadFocus = inputElement === document.activeElement;
    await this.saveSettings();
    
    if (hadFocus && inputElement) {
      setTimeout(() => inputElement.focus(), 0);
    }
  }

  async deleteRaceVessel(vesselIdx) {
    const vessel = this.settings.getRaceVessels()[vesselIdx];
    if (confirm(`Delete vessel "${vessel.getName()}"? This cannot be undone.`)) {
      this.settings.delRaceVessel(vesselIdx);
      // this.updateRaceVesselsDisplay();
      await this.saveSettings();
    }
  }
}
